import { Portal } from '@mui/material';
import { Box } from '@mui/system';
import UPButton from 'components/button/UPButton';
import GeneralConfirmModal from 'components/general-confirm-modal/GeneralConfirmModal';
import useMfaReset from 'hooks/useMfaReset';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GENERAL__MFA_RESET_BUTTON,
  GENERAL__MFA_RESET_CONFIRM,
  GENERAL__MFA_RESET_CONFIRM_DEVICES,
  GENERAL__MFA_RESET_ENROLLED,
  GENERAL__MFA_RESET_ERROR,
  GENERAL__MFA_RESET_NOT_ENROLLED,
  GENERAL__MFA_RESET_SUCCESS,
} from 'translations/constants';
import { MfaResetContainer, MfaResetStatusText } from './styles';
import { MfaResetTone, UPMfaResetActionProps } from './types';

const UPMfaResetAction = (props: UPMfaResetActionProps): JSX.Element => {
  const { userId, className } = props;
  const [t] = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const { status, isLoading, isResetting, isUnavailable, hasFailed, hasSucceeded, reset } = useMfaReset(userId);

  if (isUnavailable) return null;

  const isEnrolled = status?.isEnrolled ?? false;
  const activeDeviceCount = status?.activeDeviceCount ?? 0;

  const confirmContent =
    activeDeviceCount > 0
      ? `${t(GENERAL__MFA_RESET_CONFIRM)} ${t(GENERAL__MFA_RESET_CONFIRM_DEVICES, { count: activeDeviceCount })}`
      : t(GENERAL__MFA_RESET_CONFIRM);

  const onConfirm = (): void => {
    setIsConfirmOpen(false);
    reset();
  };

  const getStatusText = (): { tone: MfaResetTone; key: string } => {
    if (hasFailed) return { tone: 'error', key: GENERAL__MFA_RESET_ERROR };
    if (hasSucceeded) return { tone: 'success', key: GENERAL__MFA_RESET_SUCCESS };
    if (isEnrolled) return { tone: 'neutral', key: GENERAL__MFA_RESET_ENROLLED };
    return { tone: 'neutral', key: GENERAL__MFA_RESET_NOT_ENROLLED };
  };

  const statusText = getStatusText();

  return (
    <MfaResetContainer className={className}>
      <Box>
        <UPButton
          text={t(GENERAL__MFA_RESET_BUTTON)}
          outlined
          disabled={isLoading || isResetting || !isEnrolled}
          onClick={() => setIsConfirmOpen(true)}
        />
        {!isLoading && <MfaResetStatusText tone={statusText.tone}>{t(statusText.key)}</MfaResetStatusText>}
      </Box>
      {isConfirmOpen && (
        <Portal>
          <GeneralConfirmModal
            open
            content={confirmContent}
            onClose={() => setIsConfirmOpen(false)}
            onAction={onConfirm}
          />
        </Portal>
      )}
    </MfaResetContainer>
  );
};

export default UPMfaResetAction;
